import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const HERO = "https://static.prod-images.emergentagent.com/jobs/f61b621a-d7ae-4dd8-9ee8-758c73b28ef2/images/e6d250ba65b9d19c88bb2e659c82d582be50b139d874a9b1ae77e9829fe27b7b.jpeg";
const DEMO = [
  ["super_admin", "Süper Yönetici", "superadmin"], ["yonetici", "Yönetici", "yonetici"], ["muhasebe", "Muhasebe", "muhasebe"],
  ["finans", "Finans", "finans"], ["satis_muduru", "Satış Müdürü", "satismuduru"], ["satis_personeli", "Satış Personeli", "satis1"],
  ["depo", "Depo", "depo"], ["satin_alma", "İthalat Sipariş", "satinalma"], ["ithalat", "İthalat", "ithalat"],
];

function DemoAccounts({ onPick }) {
  return (
    <div className="mt-8">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Demo hesapları · Şifre: <span className="font-mono text-slate-600">Demo123!</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {DEMO.map(([key, label, local]) => (
          <button key={key} type="button" onClick={() => onPick(`${local}@istoctoptan.com`)} data-testid={`demo-account-${key}`}
            className="text-left px-2.5 py-1.5 rounded border border-slate-200 hover:border-blue-600 hover:bg-blue-50 transition-colors">
            <div className="text-[12px] font-semibold text-slate-800">{label}</div>
            <div className="text-[10px] font-mono text-slate-400 truncate">{local}@</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(false);
  useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ping`).then((r) => r.json()).then((d) => setDemo(!!d.demo)).catch(() => {}); }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="İmamoğlu A.Ş toptan depo" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="font-display font-extrabold text-xl tracking-tight">İMAMOĞLU A.Ş <span className="text-blue-400">ERP</span></div>
          <div className="max-w-md">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-blue-300 mb-4">Toptan ticaret yönetimi</div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Stok, cari ve kur tek ekranda.</h1>
            <p className="mt-4 text-slate-300 text-sm leading-relaxed">Rol bazlı yetkilendirme, TCMB kurları ve denetim kaydı ile operasyonunuzu güvenle yönetin.</p>
          </div>
          <div className="text-xs font-mono text-slate-400">{demo ? "Demo ortamı" : "İmamoğlu A.Ş"}</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="login-form">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">Giriş Yap</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Kurumsal e-posta adresiniz ve şifrenizle oturum açın.</p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ad.soyad@istoctoptan.com" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Şifre</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full h-10 bg-slate-900 hover:bg-slate-800" data-testid="login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Giriş Yap <ArrowRight className="h-4 w-4 ml-1" /></>}
            </Button>
          </div>
          {demo && <DemoAccounts onPick={(e) => { setEmail(e); setPassword("Demo123!"); }} />}
          <div className="mt-6 flex justify-center gap-4 text-[12px] text-slate-500" data-testid="login-footer">
            <Link to="/gizlilik" className="hover:underline" data-testid="login-privacy-link">Gizlilik Politikası</Link>
            <Link to="/privacy" className="hover:underline" data-testid="login-privacy-en-link">Privacy</Link>
            <Link to="/hesap-silme" className="hover:underline" data-testid="login-deletion-link">Hesap Silme</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
